import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Dialog, DialogContent, FormControlLabel, Grid } from '@material-ui/core';

import { addressesApi } from 'api/addresses';
import { catalogApi } from 'api/catalog';
import { procurementApi as api } from 'api/procurement';
import { DataTable, FormComponent, Notifications } from 'shared';

function StateCountryModal({
    country,
    countryOpts,
    editing,
    getProvinces,
    getShippers,
    handleProvChange,
    mainCountryCodes,
    provinceOpts,
    row,
    setEditing,
    setNotification,
}) {
    useEffect(() => {
        getProvinces(country.text);
    }, []);

    return (
        <Dialog open={editing} onClose={() => setEditing(false)} fullWidth width="400px">
            <DialogContent>
                <FormComponent
                    formFields={[
                        {
                            name: 'province',
                            label: 'State/Province',
                            inputElement: 'select',
                            gridValue: 6,
                            inputProps: {
                                opts: provinceOpts,
                                required: !!mainCountryCodes.includes(country.value),
                            },
                            initialValue: row.original.province,
                        },
                        {
                            name: 'country',
                            label: 'Country',
                            inputElement: 'autocomplete',
                            gridValue: 6,
                            inputProps: { opts: countryOpts, onChange: handleProvChange },
                            initialValue: country,
                        },
                    ]}
                    onSubmit={async (formData) => {
                        const sendParams = {
                            country: formData.country,
                            province: mainCountryCodes.includes(formData.country) ? formData.province : 'N/A',
                        };

                        const response = await api.updateShipper(row.original.id, sendParams);
                        if (response.status === false) {
                            let message = 'Error modifying shipper';
                            if (response) {
                                message += `: ${response.msg}`;
                            }
                            setNotification({ text: message, level: 'error' });
                        } else {
                            setNotification({ text: 'Shipper modified!', level: 'success' });
                            getShippers();
                        }

                        setEditing(false);
                    }}
                />
            </DialogContent>
        </Dialog>
    );
}

function StateCountryCell({ countryOpts, getShippers, row, setNotification }) {
    const [editing, setEditing] = useState(false);

    const OGstate = row.original.province;
    const OGcountry = row.original.country;

    const mainCountryCodes = ['US', 'MX', 'CA'];

    const [provinceOpts, setProvinceOpts] = useState([]);
    const [country, setCountry] = useState(countryOpts.find((c) => c.value === row.original.country));

    // Load state options
    const getProvinces = async (country) => {
        if (country === 'United States') {
            country = 'unitedstates';
        }

        if (country === undefined) {
            const pleaseSelect = { text: 'Please Select Country', value: 0 };
            setProvinceOpts([pleaseSelect]);
            return;
        }

        const response = await api.getProvinces(country);
        if (response.status === false) {
            setNotification({ text: `No provinces found ${response.msg}`, level: 'error' });
            const pleaseSelect = { text: 'Please Select Country', value: 0 };
            setProvinceOpts([pleaseSelect]);
            return;
        }

        const miniOpts = response.data.rows.map((row) => {
            const out = {};
            out.text = row.name;
            out.value = row.twoLetter;
            return out;
        });

        const pleaseSelect = { text: 'Please Select', value: 0 };
        setProvinceOpts([pleaseSelect].concat(miniOpts));
    };

    const handleProvChange = async (e, newValue) => {
        e.preventDefault();
        if (newValue && newValue.value) {
            setCountry(newValue);
            if (mainCountryCodes.includes(newValue.value)) {
                getProvinces(newValue.text);
            } else {
                setProvinceOpts([]);
            }
        }
    };

    return (
        <div style={{ textAlign: 'center' }}>
            <div style={{ margin: 'auto' }}>{OGstate ? `${OGstate}, ${OGcountry}` : OGcountry}</div>
            <div>
                <Button id={`StateCountry_Edit_${row.id}`} onClick={() => setEditing(true)}>
                    Edit
                </Button>
            </div>
            {editing && (
                <StateCountryModal
                    editing={editing}
                    setEditing={setEditing}
                    row={row}
                    country={country}
                    countryOpts={countryOpts}
                    provinceOpts={provinceOpts}
                    handleProvChange={handleProvChange}
                    setNotification={setNotification}
                    getShippers={getShippers}
                    mainCountryCodes={mainCountryCodes}
                    getProvinces={getProvinces}
                />
            )}
        </div>
    );
}

// Vendors
function ViewShipper() {
    const [rows, setRows] = useState([]);
    const [countryOpts, setCountryOpts] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [activeOnly, setActiveOnly] = useState(true);
    const [levelOneTaxonomyOptions, setLevelOneTaxonomyOptions] = useState([]);

    // Load country options
    const getCountries = async () => {
        const response = await addressesApi.getCountries();
        if (response.status === false) {
            setNotification({ text: `No countries found ${response.msg}`, level: 'error' });
            const pleaseSelect = { text: 'Please Select', value: 0 };
            setCountryOpts([pleaseSelect]);
            return;
        }

        const countryAsOpts = response.data.rows.map((row) => {
            const out = {};
            out.text = row.name;
            out.value = row.countryCode;
            return out;
        });

        const pleaseSelect = { text: 'Please Select', value: 0 };
        setCountryOpts([pleaseSelect].concat(countryAsOpts));
    };

    const getTaxonomy = async () => {
        const response = await catalogApi.getTaxonomyByLevel(1, 'TAXONOMY');

        if (!response.status) {
            setNotification({ text: `Could not load taxonomy ${response.msg}`, level: 'error' });
            return;
        }

        const options = response.data.categories.map((row) => ({
            text: row.name,
            value: row.id,
        }));

        setLevelOneTaxonomyOptions([{ text: '[None]', value: 0 }].concat(options));
    };

    // Load
    const getShippers = async () => {
        const response = await api.getShippers(activeOnly);
        if (response.status === false) {
            setNotification({ text: `No shippers found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }
        setRows(response.data.rows);
    };

    useEffect(() => {
        getTaxonomy();
        getShippers();
        getCountries();
    }, []);

    useEffect(() => {
        getShippers();
    }, [activeOnly]);

    const columns = [
        { accessor: 'id', Header: 'Shipper ID' },
        { accessor: 'name', Header: 'Name', editable: true },
        { accessor: 'email', Header: 'Email', editable: true },
        { accessor: 'firstName', Header: 'Rep First Name', editable: true },
        { accessor: 'lastName', Header: 'Rep Last Name', editable: true },
        { accessor: 'office', Header: 'Office', editable: true },
        { accessor: 'cell', Header: 'Cell', editable: true },
        { accessor: 'lineOne', Header: 'Line One', editable: true },
        { accessor: 'lineTwo', Header: 'Line Two', editable: true },
        { accessor: 'city', Header: 'City', editable: true },
        {
            Header: 'State/Country',
            width: '150px',
            Cell: ({ row }) => StateCountryCell({ row, countryOpts, getShippers, setNotification }),
        },
        { accessor: 'zip', Header: 'Zip', editable: true },
        {
            accessor: 'active',
            Header: 'Active',
            Cell: ({ row }) => {
                const isActive = Boolean(row.original.active);

                const handleToggle = async () => {
                    const update = { active: !isActive };

                    const response = await api.updateShipper(row.original.id, update);
                    if (response.status === false) {
                        let message = 'Error modifying shipper';
                        if (response) {
                            message += `: ${response.msg}`;
                        }
                        setNotification({ text: message, level: 'error' });
                    } else {
                        setNotification({ text: 'Shipper modified!', level: 'success' });
                        getShippers();
                    }
                };

                return (
                    <div style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={isActive} onChange={handleToggle} />
                    </div>
                );
            },
        },
        {
            accessor: 'l1TaxonomyId',
            Header: 'Category',
            editable: true,
            editProps: {
                type: 'select',
                options: levelOneTaxonomyOptions,
            },
        },
    ];

    return (
        <div>
            <h1>Shippers</h1>
            <Notifications options={notification} />
            <Grid item xs={12}>
                <FormControlLabel
                    control={
                        <Checkbox
                            id="ActiveOnly_checkbox"
                            onChange={(event) => {
                                setActiveOnly(event.target.checked);
                            }}
                            color="primary"
                            checked={activeOnly}
                        />
                    }
                    label="Show Active Only"
                />
            </Grid>
            <div>
                <DataTable
                    columns={columns}
                    data={rows}
                    editable
                    saveEdit={async ({ field, id, value }) => {
                        const update = {};
                        update[field] = value;

                        const response = await api.updateShipper(id, update);
                        if (response.status === false) {
                            let message = 'Error modifying shipper';
                            if (response) {
                                message += `: ${response.msg}`;
                            }
                            setNotification({ text: message, level: 'error' });
                        } else {
                            setNotification({ text: 'Shipper modified!', level: 'success' });
                            getShippers();
                        }
                    }}
                />
            </div>
        </div>
    );
}

export { ViewShipper };
